import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Field, Form } from "react-final-form";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { TextInput } from "../../../common/fields";
import {
  bytesToGb,
  gbToBytes,
  licenseErrorMessageId,
  validateEntitlements,
} from "../../../../helpers/EnterpriseLicenseHelper";
import useCloseWhenSaved from "./useCloseWhenSaved";

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

// Storage shows in GB to 2 decimals, so converting it back would rewrite the
// stored bytes (1500000000 shows as 1.4, which is 1503238554). While the field
// still holds what it was filled with, the stored bytes go back as they are.
const isStorageUnchanged = (values, initialValues, storedBytes) =>
  !isBlank(storedBytes) && values.storage === initialValues.storage;

const toRequest = (values, initialValues, storedBytes) => ({
  users: parseInt(values.users, 10),
  storage: isStorageUnchanged(values, initialValues, storedBytes)
    ? Number(storedBytes)
    : gbToBytes(values.storage),
});

// The form, mounted each time the dialog opens so it starts clean.
const EditEntitlementsForm = ({
  users,
  storage,
  saving,
  errorCode,
  onHide,
  onSubmit,
}) => {
  const intl = useIntl();
  // What was last sent, to tell whether the API's refusal is still about the
  // values on screen.
  const [sentJson, setSentJson] = useState(null);

  useCloseWhenSaved(saving, errorCode, onHide);

  const labels = useMemo(
    () => ({
      users: intl.formatMessage({ id: "no_of_users" }),
      storage: intl.formatMessage({ id: "storage" }),
    }),
    [intl]
  );

  const initialValues = useMemo(
    () => ({
      users: users ?? "",
      storage: isBlank(storage) ? "" : String(bytesToGb(storage)),
    }),
    [users, storage]
  );

  // An unchanged storage is the stored value, sent as it is, so it is not
  // checked: a stored 1 MB shows as 0 GB and must not block a users-only save.
  const validate = useCallback(
    (values) => {
      const codes = validateEntitlements(values);
      if (isStorageUnchanged(values, initialValues, storage)) {
        delete codes.storage;
      }
      return Object.fromEntries(
        Object.entries(codes).map(([field, id]) => [
          field,
          id === "required"
            ? intl.formatMessage(
                { id: "field_required" },
                { label: labels[field] }
              )
            : intl.formatMessage({ id }),
        ])
      );
    },
    [intl, labels, initialValues, storage]
  );

  const serverMessageId =
    (!saving && sentJson && licenseErrorMessageId(errorCode)) || null;

  const send = (values) => {
    const request = toRequest(values, initialValues, storage);
    setSentJson(JSON.stringify(request));
    onSubmit(request);
  };

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={send}
      render={({ handleSubmit, values, valid }) => (
        <form
          onSubmit={handleSubmit}
          noValidate
          data-testid="edit-entitlements-form"
        >
          <div className="grid">
            <div className="col-12 md:col-6">
              <Field
                name="users"
                component={TextInput}
                type="number"
                label={labels.users}
                placeholder={labels.users}
                required
              />
            </div>
            <div className="col-12 md:col-6">
              <Field
                name="storage"
                component={TextInput}
                type="number"
                label={labels.storage}
                placeholder={labels.storage}
                required
              />
            </div>
          </div>
          {(serverMessageId &&
            valid &&
            JSON.stringify(toRequest(values, initialValues, storage)) ===
              sentJson && (
              <div
                className="edexlms-field-error"
                role="alert"
                data-testid="edit-entitlements-error"
              >
                {intl.formatMessage({ id: serverMessageId })}
              </div>
            )) ||
            null}
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              outlined
              label={intl.formatMessage({ id: "cancel" })}
              onClick={onHide}
              disabled={saving}
              data-testid="edit-entitlements-cancel"
            />
            <Button
              type="submit"
              label={intl.formatMessage({ id: "update_entitlements" })}
              loading={saving}
              disabled={saving}
              data-testid="edit-entitlements-submit"
            />
          </div>
        </form>
      )}
    />
  );
};

// Changes the user limit and the storage (entered in GB, sent in bytes).
const EditEntitlementsDialog = ({ visible, onHide, ...formProps }) => {
  const intl = useIntl();
  // No close icon or Escape while saving: a refusal the form explains is not
  // a toast, so closing before the API answers would lose it.
  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      closable={!formProps.saving}
      closeOnEscape={!formProps.saving}
      header={intl.formatMessage({ id: "edit_entitlements" })}
      style={{ width: "36rem" }}
      breakpoints={{ "640px": "calc(100vw - 2rem)" }}
    >
      {(visible && <EditEntitlementsForm onHide={onHide} {...formProps} />) ||
        null}
    </Dialog>
  );
};

EditEntitlementsForm.propTypes = {
  users: PropTypes.string,
  storage: PropTypes.string,
  saving: PropTypes.bool,
  errorCode: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

EditEntitlementsDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  users: PropTypes.string,
  storage: PropTypes.string,
  saving: PropTypes.bool,
  errorCode: PropTypes.string,
};

export default EditEntitlementsDialog;
